<template>
  <div
    v-if="metadata"
    class="pagination -bottom-[50px] flex w-full flex-1 items-center justify-between rounded-b-sm bg-white px-6 py-[11px] text-sm font-medium text-gray-500 shadow-main"
    :class="{ absolute: isAbsolute }"
    data-cy="pagination"
  >
    <!-- to fake flex between -->
    <div v-if="!showPerPage" />

    <div v-if="showPerPage" class="flex h-7 items-center gap-x-2">
      <span class="text-headline">{{ $t("PAGINATION.ITEMS_PER_PAGE") }}:</span>
      <lf-dropdown
        :custom-button-classnames="{
          heightClassname: 'h-7',
        }"
        name="items_per_page"
        :hide-placeholder-when-value="true"
        :options="ITEMS_PER_PAGE"
        :value="metadata.per_page || 15"
        :drop-up="true"
        @change="changeItemsPerPage"
      />
    </div>
    <div
      v-if="metadata.last_page"
      class="pagination flex h-7 items-stretch gap-x-2"
    >
      <span class="mr-6 leading-7" data-cy="total">
        {{ $t("PAGINATION.TOTAL") }}: {{ metadata.total }}
      </span>
      <a
        v-show="metadata.current_page > 1"
        class="pagination-arrow pagination-arrow__left mr-2 cursor-pointer"
        @click.prevent="
          metadata?.current_page && changePage(metadata.current_page - 1)
        "
      >
        &nbsp;
      </a>
      <a
        :class="{ 'bg-gray-100': 1 == metadata.current_page }"
        class="page"
        @click.prevent="changePage(1)"
      >
        1
      </a>
      <span v-if="metadata.current_page > 4" class="ellipsis">&hellip;</span>
      <a
        v-for="page in pageNumbersToShow"
        :key="page"
        :class="{ 'bg-gray-100': page == metadata.current_page }"
        class="page"
        @click.prevent="changePage(page)"
      >
        {{ page }}
      </a>
      <span
        v-if="metadata.current_page + 3 < metadata.last_page"
        class="ellipsis ml-2"
      >
        &hellip;
      </span>
      <a
        v-if="metadata.last_page > 1"
        :class="{
          'bg-gray-100': metadata.last_page == metadata.current_page,
        }"
        class="page"
        @click.prevent="metadata?.last_page && changePage(metadata.last_page)"
      >
        {{ metadata.last_page }}
      </a>
      <a
        v-show="metadata.current_page != metadata.last_page"
        class="pagination-arrow pagination-arrow__right ml-2 cursor-pointer"
        @click.prevent="
          metadata?.current_page && changePage(metadata.current_page + 1)
        "
      >
        &nbsp;
      </a>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";

import range from "lodash/range";

import LfDropdown from "../inputs/LfDropdown.vue";

import type { IResponseMeta } from "@/models/common";
import type { PropType } from "vue";

const emit = defineEmits<{
  (event: "changePage", value: number): void;
  (event: "changeItemsPerPage", perPage: number, maxPage: number | null): void;
}>();

const props = defineProps({
  metadata: {
    type: Object as PropType<IResponseMeta | null>,
    required: true,
  },
  showPerPage: {
    type: Boolean,
    required: true,
  },
  isAbsolute: {
    type: Boolean,
    default: true,
  },
});

const ITEMS_PER_PAGE = {
  15: "15",
  25: "25",
  50: "50",
  100: "100",
};

const PAGINATION_NUMBERS_SPACING = 2;
const PAGINATION_INITIAL_START = 2;

const pageNumbersToShow = computed(() => {
  if (!props.metadata) {
    return [];
  }
  const { current_page, last_page } = props.metadata;
  if (last_page <= 2) {
    // It means that there's nothing in between
    return [];
  }

  let from = current_page - PAGINATION_NUMBERS_SPACING;
  let to = current_page + PAGINATION_NUMBERS_SPACING;

  if (from <= 1) {
    from = PAGINATION_INITIAL_START;
  }

  if (to >= last_page) {
    to = last_page - 1;
  }

  return range(from, to + 1);
});

const changePage = (page: number) => {
  emit("changePage", page);
};

const changeItemsPerPage = (itemsPerPage: number | string) => {
  const val = Number(itemsPerPage);
  if (!props.metadata) {
    return;
  }
  const maxPage = Math.ceil(props.metadata.total / val);
  if (maxPage < props.metadata.current_page) {
    emit("changeItemsPerPage", val, maxPage);
  } else {
    emit("changeItemsPerPage", val, null);
  }
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.pagination-arrow {
  @apply h-0 w-0 self-center;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
}

.pagination-arrow__left {
  border-right: 6px solid #d4d8e2;
}
.pagination-arrow__right {
  border-left: 6px solid #d4d8e2;
}

.pagination {
  .page {
    @apply flex h-7 min-w-7 cursor-pointer items-center justify-center rounded-sm border border-gray-100 px-2;
  }
  .ellipsis {
    @apply rounded-sm border border-gray-100 px-2;
  }
}
</style>
